/**
 * When complaints were said: the "Since" cut-off, the hidden-as-older count,
 * and each complaint's sources by year for the timeline. Pure, so it is tested
 * without rendering. The dates come from the server (`boardroom/said.py`).
 */
import type { BoardroomPainRow } from '../../../types';

export type Since = 'all' | '2y' | '1y';

export const SINCE_WORDS: Record<Since, string> = { all: 'All', '2y': '2 years', '1y': '1 year' };

/** The first day still inside the cut-off, as "YYYY-MM-DD"; null for all. */
export function cutoff(since: Since, now: Date = new Date()): string | null {
  if (since === 'all') return null;
  const years = since === '2y' ? 2 : 1;
  const d = new Date(Date.UTC(now.getUTCFullYear() - years, now.getUTCMonth(), now.getUTCDate()));
  return d.toISOString().slice(0, 10);
}

/** Whether a said date ("2024", "2024-03", "2024-03-05") is before the cut-off.
 *  A year or month alone counts from its last day, so "2024" is still in for
 *  a cut-off in mid 2024. */
export function isOlder(when: string | null | undefined, cut: string | null): boolean {
  if (!when || !cut) return false;
  const end = when.length === 4 ? `${when}-12-31` : when.length === 7 ? `${when}-31` : when;
  return end < cut;
}

/** The complaints shown under a cut-off, and how many fold away as older.
 *  A complaint with no date at all stays: unknown is not old. */
export function splitBySince(
  rows: BoardroomPainRow[],
  cut: string | null,
): { shown: BoardroomPainRow[]; older: BoardroomPainRow[] } {
  const shown: BoardroomPainRow[] = [];
  const older: BoardroomPainRow[] = [];
  for (const r of rows) (isOlder(r.last_heard, cut) ? older : shown).push(r);
  return { shown, older };
}

/** "Mar 2026", "2021", or "" for no date. */
export function heardLabel(when: string | null | undefined): string {
  if (!when) return '';
  if (when.length === 4) return when;
  const [y, m] = when.split('-');
  const month = new Date(Date.UTC(Number(y), Number(m) - 1, 1)).toLocaleString('en-US', {
    month: 'short',
    timeZone: 'UTC',
  });
  return `${month} ${y}`;
}

export interface TimelineDot {
  url: string;
  quote: string | null;
  when: string;
  /** How the date is known: off the page by a seat, or the page's own date. */
  from: string | null;
  seller: boolean;
}

/** The years the timeline spans, oldest first, from every dated source. */
export function timelineYears(rows: BoardroomPainRow[], now: Date = new Date()): number[] {
  const years = rows
    .flatMap(r => r.sources.map(s => s.said_at).filter((d): d is string => !!d))
    .map(d => Number(d.slice(0, 4)));
  if (!years.length) return [];
  const first = Math.min(...years);
  const last = Math.max(now.getUTCFullYear(), ...years);
  return Array.from({ length: last - first + 1 }, (_, i) => first + i);
}

/** One complaint's dated sources by year, and how many have no date. */
export function dotsByYear(row: BoardroomPainRow): { byYear: Map<number, TimelineDot[]>; undated: number } {
  const byYear = new Map<number, TimelineDot[]>();
  let undated = 0;
  for (const s of row.sources) {
    if (!s.said_at) {
      undated += 1;
      continue;
    }
    const year = Number(s.said_at.slice(0, 4));
    const dot = { url: s.url, quote: s.quote ?? null, when: s.said_at, from: s.said_from ?? null, seller: !!s.seller };
    byYear.set(year, [...(byYear.get(year) ?? []), dot]);
  }
  return { byYear, undated };
}
